import React from "react";
import { PencilRuler, Package, Building2, Compass } from "lucide-react";

import { PageShell } from "../components/layout/PageShell";
import { Container } from "../components/zen/Container";
import { Section } from "../components/zen/Section";
import { PageHero } from "../components/zen/PageHero";
import { FeatureRow } from "../components/zen/FeatureRow";
import { ClosingCTA } from "../components/zen/ClosingCTA";

const DIVISIONS = [
    {
        eyebrow: "Division 01",
        title: "BIM & Engineering Services",
        icon: PencilRuler,
        paragraphs: [
            "Multidisciplinary BIM, drafting, MEP, and engineering production support delivered under U.S. project management. We help engineering firms, contractors, and architects expand production capacity without adding permanent headcount.",
            "Our teams work as a dedicated extension of yours — learning your standards, adopting your workflows, and delivering coordinated multidisciplinary output through a single U.S.-based point of contact.",
        ],
        capabilities: [
            "Revit & BIM production",
            "Multidisciplinary drafting",
            "US-managed delivery",
        ],
        cta: { label: "Explore Services", href: "/bim" },
    },
    {
        eyebrow: "Division 02",
        title: "Building Materials",
        icon: Package,
        logoSrc: "/logos/citywood.png",
        logoAlt: "CityWood — wholesale timber supply",
        logoMaxWidth: 280,
        paragraphs: [
            "Wholesale supply of rough pine, hardwood, and timber to commercial buyers, contractors, and industrial partners across the region.",
            "We operate as a direct-to-buyer supply channel — competitive pricing on volume, custom-cut dimensions, and reliable freight scheduling for sustained-demand customers.",
        ],
        capabilities: [
            "Rough pine & hardwood",
            "Custom dimensions",
            "Logistics & freight",
        ],
        cta: { label: "Request Pricing", href: "/building-materials" },
    },
    {
        eyebrow: "Division 03",
        title: "Real Estate Development",
        icon: Building2,
        paragraphs: [
            "Off-market acquisitions and direct property solutions across residential and small commercial assets — backed by operator-led diligence and long-term hold discipline.",
            "We work directly with sellers seeking a quiet, efficient transaction. Closings are flexible to seller timelines, with all-cash and creative structures available.",
        ],
        capabilities: [
            "Off-market acquisitions",
            "Direct purchase program",
            "Development opportunities",
        ],
        cta: { label: "Submit Property", href: "/real-estate" },
    },
    {
        eyebrow: "Division 04",
        title: "Business Consulting",
        icon: Compass,
        logoSrc: "/logos/zenme-z-mark.png",
        logoAlt: "ZenMe Group corporate mark",
        logoMaxWidth: 380,
        paragraphs: [
            "Operational advisory and growth strategy for founders and management teams — covering systems, financial operations, and scalable execution.",
            "Engagements pair diagnostic depth with hands-on implementation. We don't just deliver decks — we partner through execution to make the work stick.",
        ],
        capabilities: [
            "Operational systems",
            "Financial strategy",
            "Growth execution",
        ],
        cta: { label: "Schedule Consultation", href: "/consulting" },
    },
];

export default function Divisions() {
    return (
        <PageShell>
            <div data-testid="page-divisions">
                <PageHero
                    eyebrow="Our Portfolio"
                    title="Specialized businesses. Coordinated execution."
                    subtitle="ZenMe Group LLC operates across four complementary divisions — each built to deliver enduring value through operational excellence, disciplined delivery, and direct partnership."
                    data-testid="divisions-hero"
                />

                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="space-y-28 lg:space-y-40">
                            {DIVISIONS.map((d, i) => (
                                <FeatureRow
                                    key={d.title}
                                    {...d}
                                    reverse={i % 2 === 1}
                                    data-testid={`feature-row-${i}`}
                                />
                            ))}
                        </div>
                    </Container>
                </Section>

                <ClosingCTA
                    eyebrow="Get In Touch"
                    title="Looking for the right division to partner with?"
                    subtitle="Our team will route your inquiry to the appropriate division and respond within one business day."
                    primary={{ label: "Contact Us", to: "/contact" }}
                    secondary={{ label: "Back to Home", to: "/" }}
                    data-testid="divisions-closing-cta"
                />
            </div>
        </PageShell>
    );
}
